import * as React from "react";
import {alpha, Box, Grid, Paper, Stack, Typography} from "@mui/material";
import PropTypes from "prop-types";

PivanTokenAnnotator.propTypes = {
    text: PropTypes.shape({
        lines: PropTypes.array.isRequired
    }),
    labelsToApply: PropTypes.array,
    onChange: PropTypes.func,
    onTokenSelection: PropTypes.func
}

PivanTokenAnnotator.defaultProps = {
    labelsToApply: [],
    onChange: () => null,
    onTokenSelection: () => null
}

export default function PivanTokenAnnotator(props) {
    const {text, labelsToApply, onChange, onTokenSelection} = props;
    const [tokens, setTokens] = React.useState([]);
    const [mouseDownFlag, setMouseDownFlag] = React.useState(false);
    const [selectionStarted, setSelectionStarted] = React.useState();
    const [selectedTokens, setSelectedTokens] = React.useState(new Set());

    React.useEffect(() => {
        onTokenSelection([...selectedTokens]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTokens])

    React.useEffect(() => {
        const getTokens = (data) => {
            let tokens = [];
            Array.isArray(data.lines) && data.lines.forEach((line) => {
                Array.isArray(line.tokens) && line.tokens.forEach((token) => {
                    tokens.push(token);
                });
            });
            return tokens;
        }
        if (text) {
            setTokens(getTokens(text));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text]);

    const selectionIsEmpty = (selection) => {
        let position = selection.anchorNode.compareDocumentPosition(selection.focusNode);
        return position === 0 && selection.focusOffset === selection.anchorOffset;
    }

    const selectionIsBackwards = (selection) => {
        if (selectionIsEmpty(selection)) return false

        let position = selection.anchorNode.compareDocumentPosition(selection.focusNode)
        return ((!position && selection.anchorOffset > selection.focusOffset)
            || position === Node.DOCUMENT_POSITION_PRECEDING);
    }

    const applyLabelsToTokens = (selectedTokens) => {
        selectedTokens.forEach(selectedToken => {
            let token = tokens.find(t => t.id === selectedToken.id);
            if (token) {
                let elemToApply = labelsToApply.filter(el => !token.labels.find(tl => tl.id === el.id));
                elemToApply.forEach(label => {
                    token.labels.push(label);
                });
                onChange(token, elemToApply);
            }
        });
        setTokens(prevState => [...prevState]);
    }

    const handleMouseUp = (event) => {
        const selection = window.getSelection();
        if (selectionIsEmpty(selection))
            return;

        // check if selection is valid
        if (!selection.anchorNode.parentElement.hasAttribute('data-i')
            || !selection.focusNode.parentElement.hasAttribute('data-i')) {
            window.getSelection().empty();
            return;
        }

        let start = parseInt(selection.anchorNode.parentElement.getAttribute('data-i'), 10);
        let end = parseInt(selection.focusNode.parentElement.getAttribute('data-i'), 10);
        if (selectionIsBackwards(selection)) {
            ;[start, end] = [end, start]
        }
        end += 1;
        let selectedTokens = tokens.slice(start, end);
        if (event.shiftKey) {
            setSelectedTokens(new Set(selectedTokens));
        } else {
            applyLabelsToTokens(selectedTokens);
        }
        window.getSelection().empty();
    }

    const handleSelectionStarted = () => {
        if (mouseDownFlag)
            setSelectionStarted(true);
    }

    const resetSelection = () => {
        setMouseDownFlag(false);
        setSelectionStarted(false);
    }

    const handleClickToken = (event, token) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) return;
        if (event.ctrlKey) {
            setSelectedTokens(prevState => new Set(prevState).add(token));
        } else {
            setSelectedTokens(new Set().add(token));
        }
    }

    const handleClickAway = () => {
        if (selectedTokens.size > 0)
            setSelectedTokens(new Set());
    }

    return (
        <Paper
            onMouseUp={handleMouseUp}
            onClick={handleClickAway}
            elevation={0}
            sx={{
                overflowY: "auto",
                overflowX: "hidden",
                position: "relative",
                width: "100%",
                height: "100%",
                minHeight: "200px"
            }}>
            <Box sx={{
                m: 3,
                position: "absolute",
                overflowY: 'auto',
                overflowX: "hidden",
            }}>
                <Grid container rowSpacing={1} justifyContent={"space-between"}>
                    {tokens.map((token, index) => (
                        <Grid item key={`token-annotator-${token.id}`} flexGrow={1}>
                            <Stack spacing="2px">
                                <Typography
                                    textAlign="center"
                                    data-i={index}
                                    fontSize={20}
                                    onClick={(event) => handleClickToken(event, token)}
                                    onMouseDown={() => setMouseDownFlag(true)}
                                    onMouseMove={handleSelectionStarted}
                                    onMouseUp={resetSelection}
                                    sx={{
                                        borderRadius: "0.2em",
                                        bgcolor: theme => selectedTokens.has(token)
                                            ? alpha(theme.palette.secondary.main, 0.4)
                                            : undefined,
                                        ":hover": {
                                            cursor: selectionStarted ? "" : "default",
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.2)
                                        }
                                    }}>{(token.type !== "PUNCTUATION" ? "\u00A0" : "") + token.textContent}</Typography>
                                {token.labels.map((label, index) => (
                                    <span key={`token${token.id}-annotation${index}`} style={{
                                        backgroundColor: label.color,
                                        height: "4px",
                                        position: "relative",
                                        width: "100%",
                                        marginBottom: "1px",
                                    }}/>))}
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Paper>
    );
}